import { Injectable, signal } from '@angular/core';
import PocketBase, { RecordSubscribeOptions, RecordSubscription, UnsubscribeFunc } from 'pocketbase';
import { environment } from '../../environments/environment';
import { TableType } from '../enums/TableType';
import Category from '../models/Category.model';
import Floor from '../models/Floor.model';
import Menu from '../models/Menu.model';
import { NotificationRequest, NotificationResponse } from '../models/Notification.model';
import { OnlineOrder } from '../models/OnlineOrder.model';
import Option from '../models/Option.model';
import OptionGroup from '../models/OptionGroup.model';
import Product from '../models/Product.model';
import Settings from '../models/Settings.model';
import Table from '../models/Table.model';

@Injectable({ providedIn: 'root' })
export class PBService {
  public pb: PocketBase;
  public loading = signal(false);
  public pricingTier = signal<TableType>(TableType.TABLE);
  private activeRequests = 0;

  constructor() {
    this.pb = new PocketBase(environment.API_URL);

    const collectionsWithViewIndexSort = [
      'Menu',
      'Category',
      'OptionGroup',
      'Option',
      'Product',
      'Staff',
      'Device',
      'Printer',
      'Point',
      'Floor',
      'Table',
    ];

    const collectionsWithSoftDelete = [
      'Menu',
      'Category',
      'OptionGroup',
      'Option',
      'Product',
      'Staff',
      'Device',
      'Printer',
      'Point',
      'Floor',
      'Table',
    ];

    this.pb.beforeSend = (url, options) => {
      this.activeRequests++;
      this.loading.set(true);

      options.headers = {
        ...options.headers,
        api_key: environment.API_KEY,
      };

      try {
        const urlObject = new URL(url);
        const pathParts = urlObject.pathname.split('/');
        const collectionName = pathParts[pathParts.length - 2];

        const isGetRecordsRequest =
          urlObject.pathname.endsWith('/records') &&
          (!options.method || options.method.toUpperCase() === 'GET');

        if (isGetRecordsRequest) {
          // Handle Soft Delete
          if (collectionsWithSoftDelete.includes(collectionName)) {
            const softDeleteFilter = 'deleted = null';

            if (options['query']!['filter'] && options['query']!['filter'].trim() !== '') {
              // If a filter already exists in the options, append to it
              options['query']!['filter'] = `((${options['query']!['filter']
                }) && ${softDeleteFilter})`;
            } else {
              // Otherwise, just set the soft delete filter in the options
              options['query']!['filter'] = softDeleteFilter;
            }
          }

          // Handle ViewIndex Sorting
          if (collectionsWithViewIndexSort.includes(collectionName) && !options['query']!['sort']) {
            options['query']!['sort'] = 'ViewIndex';
          }
        }
      } catch (e) {
        console.error('Could not modify request options:', e);
      }

      return { url, options };
    };

    this.pb.afterSend = (response: Response, data: any, requestOptions?: any) => {
      this.activeRequests--;
      if (this.activeRequests <= 0) {
        this.loading.set(false);
      }
      return data;
    };
  }

  async getMenus({
    customerID,
    page = 1,
    perPage = 25,
    filter,
  }: GetMenusOptions): Promise<PaginatedResult<Menu>> {
    let filterParts: string[] = [];

    if (customerID && customerID.trim() !== '') {
      filterParts.push(`VenueID = "${customerID}" && VisibilityException.QR_MENU != true`);
    }

    if (filter && filter.trim() !== '') {
      filterParts.push(`Name ~ "${filter}" || Description ~ "${filter}" || SID ~ "${filter}"`);
    }

    const filterString = filterParts.join(' && ');

    const fetchParams = {
      ...(filterString && { filter: filterString }),
      requestKey: null,
      expand: 'CurrencyID',
    };

    const resultList = await this.pb.collection('Menu').getList(page, perPage, fetchParams);

    const menus: Menu[] = resultList.items.map((menu: any) => {
      if (menu.expand?.CurrencyID) {
        menu.Currency = menu.expand.CurrencyID;
      }
      delete menu.expand;
      return menu as Menu;
    });

    return {
      items: menus,
      page: resultList.page,
      perPage: resultList.perPage,
      totalItems: resultList.totalItems,
      totalPages: resultList.totalPages,
    };
  }

  async getCategories({
    customerID,
    menuID,
    parentCategoryID,
    page = 1,
    perPage = 25,
    filter,
  }: GetCategoriesOptions): Promise<PaginatedResult<Category>> {
    let filterParts: string[] = [];

    if (customerID && customerID.trim() !== '') {
      filterParts.push(`VenueID = "${customerID}" && VisibilityException.QR_MENU != true`);
    }

    if (menuID && menuID.trim() !== '') {
      filterParts.push(`MenuID = "${menuID}"`);
    }

    if (parentCategoryID === null) {
      // Fetch Root Categories (Parent is empty)
      filterParts.push(`ParentCategoryID = ""`);
    } else if (parentCategoryID) {
      // Fetch Sub Categories
      filterParts.push(`ParentCategoryID = "${parentCategoryID}"`);
    }

    if (filter && filter.trim() !== '') {
      filterParts.push(`Name ~ "${filter}" || Description ~ "${filter}" || SID ~ "${filter}"`);
    }

    const filterString = filterParts.join(' && ');

    const fetchParams = {
      ...(filterString && { filter: filterString }),
      requestKey: null,
    };

    const resultList = await this.pb.collection('Category').getList(page, perPage, fetchParams);
    return {
      items: resultList.items as Category[],
      page: resultList.page,
      perPage: resultList.perPage,
      totalItems: resultList.totalItems,
      totalPages: resultList.totalPages,
    };
  }

  async getCategory(id: string): Promise<Category | null> {
    try {
      const category = await this.pb.collection('Category').getOne(id, { requestKey: null });
      return category as unknown as Category;
    } catch (error) {
      console.error(`Error fetching category with id ${id}:`, error);
      return null;
    }
  }

  async getAllCategories(customerID: string, menuID: string): Promise<Category[]> {
    try {
      const filterParts: string[] = [];
      if (customerID && customerID.trim() !== '') {
        filterParts.push(`VenueID = "${customerID}" && VisibilityException.QR_MENU != true`);
      }
      if (menuID && menuID.trim() !== '') {
        filterParts.push(`MenuID = "${menuID}"`);
      }
      const filterString = filterParts.join(' && ');
      const records = await this.pb.collection('Category').getFullList({
        filter: filterString || undefined,
        requestKey: null,
      });
      return records as unknown as Category[];
    } catch (error) {
      console.error(`Error fetching all categories for menu ${menuID}:`, error);
      return [];
    }
  }

  async getProducts({
    customerID,
    menuID,
    categoryID,
    page = 1,
    perPage = 25,
    filter,
  }: GetProductsOptions): Promise<PaginatedResult<Product>> {
    let filterParts: string[] = [];

    if (customerID && customerID.trim() !== '') {
      filterParts.push(`VenueID = "${customerID}" && VisibilityException.QR_MENU != true`);
    }

    if (menuID && menuID.trim() !== '') {
      filterParts.push(`MenuID = "${menuID}"`);
    }

    if (categoryID && categoryID.trim() !== '' && categoryID.trim() !== '*') {
      filterParts.push(`CategoryID ~ "${categoryID}"`);
    }

    if (filter && filter.trim() !== '') {
      filterParts.push(`Name ~ "${filter}" || Description ~ "${filter}" || SID ~ "${filter}"`);
    }

    const filterString = filterParts.join(' && ');

    const fetchParams = {
      ...(filterString && { filter: filterString }),
      requestKey: null,
      expand: 'ProductOption_via_ProductID, AllergenID',
    };

    const result = await this.pb.collection('Product').getList(page, perPage, fetchParams);

    const products: Product[] = result.items.map((product: any) => {
      if (product.expand?.ProductOption_via_ProductID) {
        product.ProductOptions = product.expand.ProductOption_via_ProductID;
      }
      if (product.expand?.AllergenID) {
        product.Allergens = product.expand.AllergenID;
      }
      delete product.expand;

      const tier = this.pricingTier();
      let price = 0;
      let tax = 0;

      switch (tier) {
        case TableType.PACKAGE:
          price = typeof product.Price2 === 'number' && product.Price2 > 0 ? product.Price2 : (product.Price1 || 0);
          tax = typeof product.Tax2 === 'number' && product.Tax2 > 0 ? product.Tax2 : (product.Tax1 || 0);
          break;
        case TableType.REGISTER:
          price = typeof product.Price3 === 'number' && product.Price3 > 0 ? product.Price3 : (product.Price1 || 0);
          tax = typeof product.Tax3 === 'number' && product.Tax3 > 0 ? product.Tax3 : (product.Tax1 || 0);
          break;
        case TableType.TABLE:
        default:
          price = typeof product.Price1 === 'number' ? product.Price1 : 0;
          tax = typeof product.Tax1 === 'number' ? product.Tax1 : 0;
          break;
      }

      product.grossPrice = price * (1 + tax / 100);
      product.resolvedTax = tax;

      return product as Product;
    });

    return {
      items: products,
      page: result.page,
      perPage: result.perPage,
      totalItems: result.totalItems,
      totalPages: result.totalPages,
    };
  }

  async getProduct(id: string): Promise<Product | undefined> {
    if (id && id.trim() === '') {
      return undefined;
    }

    const fetchParams = {
      requestKey: null,
      expand: 'ProductOption_via_ProductID, AllergenID',
    };

    const result = await this.pb.collection('Product').getOne(id, fetchParams);

    if (result.expand?.['ProductOption_via_ProductID']) {
      result['ProductOptions'] = result.expand['ProductOption_via_ProductID'];
    }
    if (result.expand?.['AllergenID']) {
      result['Allergens'] = result.expand['AllergenID'];
    }

    delete result.expand;

    const tier = this.pricingTier();
    let price = 0;
    let tax = 0;

    switch (tier) {
      case TableType.PACKAGE:
        price = typeof result['Price2'] === 'number' && result['Price2'] > 0 ? result['Price2'] : (result['Price1'] || 0);
        tax = typeof result['Tax2'] === 'number' && result['Tax2'] > 0 ? result['Tax2'] : (result['Tax1'] || 0);
        break;
      case TableType.REGISTER:
        price = typeof result['Price3'] === 'number' && result['Price3'] > 0 ? result['Price3'] : (result['Price1'] || 0);
        tax = typeof result['Tax3'] === 'number' && result['Tax3'] > 0 ? result['Tax3'] : (result['Tax1'] || 0);
        break;
      case TableType.TABLE:
      default:
        price = typeof result['Price1'] === 'number' ? result['Price1'] : 0;
        tax = typeof result['Tax1'] === 'number' ? result['Tax1'] : 0;
        break;
    }

    result['grossPrice'] = price * (1 + tax / 100);
    result['resolvedTax'] = tax;

    return result as Product;
  }

  async getOptionGroups({
    customerID,
    menuID,
    page = 1,
    perPage = 25,
    filter,
  }: GetOptionGroupsOptions): Promise<PaginatedResult<OptionGroup>> {
    let filterParts: string[] = [];

    if (customerID && customerID.trim() !== '') {
      filterParts.push(`VenueID = "${customerID}"`);
    }

    if (menuID && menuID.trim() !== '') {
      filterParts.push(`MenuID = "${menuID}"`);
    }

    if (filter && filter.trim() !== '') {
      filterParts.push(`Name ~ "${filter}" || Description ~ "${filter}" || SID ~ "${filter}"`);
    }

    const filterString = filterParts.join(' && ');

    const fetchParams = {
      ...(filterString && { filter: filterString }),
      requestKey: null,
      expand: 'Option_via_OptionGroupID',
    };

    const resultList = await this.pb.collection('OptionGroup').getList(page, perPage, fetchParams);

    const optionGroups: OptionGroup[] = resultList.items.map((group: any) => {
      if (group.expand && group.expand.Option_via_OptionGroupID) {
        group.Options = group.expand.Option_via_OptionGroupID;
      }
      delete group.expand;
      return group as OptionGroup;
    });

    return {
      items: optionGroups,
      page: resultList.page,
      perPage: resultList.perPage,
      totalItems: resultList.totalItems,
      totalPages: resultList.totalPages,
    };
  }

  async getOptions({
    customerID,
    optionGroupID,
    page = 1,
    perPage = 25,
  }: GetOptionsOptions): Promise<PaginatedResult<Option>> {
    let filterParts: string[] = [];

    if (customerID && customerID.trim() !== '') {
      filterParts.push(`VenueID = "${customerID}"`);
    }

    if (optionGroupID && optionGroupID.trim() !== '') {
      filterParts.push(`OptionGroupID = "${optionGroupID}"`);
    }

    const filterString = filterParts.join(' && ');

    const fetchParams = {
      ...(filterString && { filter: filterString }),
      requestKey: null,
    };

    const resultList = await this.pb.collection('Option').getList(page, perPage, fetchParams);

    return {
      items: resultList.items as Option[],
      page: resultList.page,
      perPage: resultList.perPage,
      totalItems: resultList.totalItems,
      totalPages: resultList.totalPages,
    };
  }

  async getSettings(customerID: string): Promise<Settings[]> {
    const expandParams = [
      'VenueID',
      'DefaultCurrencyID',
      'AllowedCurrencyIDs',
      'DefaultLanguageID',
      'AllowedLanguageIDs',
      'VenueID.Licence_via_VenueID',
    ];

    const result = await this.pb.collection('Settings').getList(1, 1, {
      expand: expandParams.join(','),
      filter: `VenueID = "${customerID}"`,
      requestKey: null,
    });

    const settings: Settings[] = result.items.map((setting: any) => {
      // Process Customer
      if ((setting.expand?.VenueID || setting.expand?.CustomerID)) {
        const vRec = setting.expand.VenueID || setting.expand.CustomerID;
        setting.Venue = vRec;
        setting.Customer = vRec;

        // Process nested Licences
        if ((setting.Venue.expand?.Licence_via_VenueID || setting.Venue.expand?.Licence_via_CustomerID)) {
          const lic = setting.Venue.expand?.Licence_via_VenueID || setting.Venue.expand?.Licence_via_CustomerID;
          setting.Venue.Licences = lic;
          setting.Customer.Licences = lic;
        }
      }

      // Process Currencies
      if (setting.expand?.DefaultCurrencyID) {
        setting.DefaultCurrency = setting.expand.DefaultCurrencyID;
        setting.Currency = setting.DefaultCurrency;
      }
      if (setting.expand?.AllowedCurrencyIDs) {
        setting.AllowedCurrencies = Array.isArray(setting.expand.AllowedCurrencyIDs)
          ? setting.expand.AllowedCurrencyIDs
          : [setting.expand.AllowedCurrencyIDs];
      }

      // Process Languages
      if (setting.expand?.DefaultLanguageID) {
        setting.DefaultLanguage = setting.expand.DefaultLanguageID;
      }
      if (setting.expand?.AllowedLanguageIDs) {
        setting.AllowedLanguages = Array.isArray(setting.expand.AllowedLanguageIDs)
          ? setting.expand.AllowedLanguageIDs
          : [setting.expand.AllowedLanguageIDs];
      }

      // Merge available languages: DefaultLanguage first, then AllowedLanguages
      const allLanguages: any[] = [];
      if (setting.DefaultLanguage) {
        allLanguages.push(setting.DefaultLanguage);
      }
      if (setting.AllowedLanguages && Array.isArray(setting.AllowedLanguages)) {
        for (const lang of setting.AllowedLanguages) {
          if (!allLanguages.some((l) => l.id === lang.id)) {
            allLanguages.push(lang);
          }
        }
      }
      setting.Language = allLanguages;

      delete setting.expand;
      return setting as Settings;
    });

    return settings as Settings[];
  }

  async getTable(id: string): Promise<Table | null> {
    try {
      return (await this.pb.collection('Table').getOne(id, { requestKey: null })) as any as Table;
    } catch (error) {
      console.error('Error fetching table:', error);
      return null;
    }
  }

  async getFloor(id: string): Promise<Floor | null> {
    try {
      return (await this.pb.collection('Floor').getOne(id, { requestKey: null })) as any as Floor;
    } catch (error) {
      console.error('Error fetching floor:', error);
      return null;
    }
  }

  async getOnlineOrders(
    options: GetOnlineOrdersOptions = {}
  ): Promise<PaginatedResult<OnlineOrder>> {
    const { page = 1, perPage = 50, filter, sort = 'created', expand } = options;
    const fetchParams: Record<string, any> = {
      requestKey: null,
      sort,
    };
    if (filter && filter.trim() !== '') {
      fetchParams['filter'] = filter;
    }
    if (expand) {
      fetchParams['expand'] = expand;
    }

    const resultList = await this.pb.collection('OnlineOrder').getList(page, perPage, fetchParams);
    return {
      items: resultList.items as unknown as OnlineOrder[],
      page: resultList.page,
      perPage: resultList.perPage,
      totalItems: resultList.totalItems,
      totalPages: resultList.totalPages,
    };
  }

  async getOnlineOrder(id: string, expand?: string): Promise<OnlineOrder | null> {
    if (!id || id.trim() === '') {
      return null;
    }
    try {
      const fetchParams: Record<string, any> = { requestKey: null };
      if (expand) {
        fetchParams['expand'] = expand;
      }
      return (await this.pb
        .collection('OnlineOrder')
        .getOne(id, fetchParams)) as unknown as OnlineOrder;
    } catch (error) {
      console.error(`Error fetching online order ${id}:`, error);
      return null;
    }
  }

  async createOnlineOrder(payload: any): Promise<OnlineOrder> {
    try {
      return await this.pb.send<OnlineOrder>('/v1/online-order/qr-menu', {
        method: 'POST',
        body: payload,
      });
    } catch (err: any) {
      let errDetail = 'Failed to submit online order.';
      const res = err?.response || err?.data;
      if (res) {
        errDetail = res.message || res.error || (res.data && res.data.message) || errDetail;
      } else if (err?.message) {
        errDetail = err.message;
      }
      throw new Error(errDetail);
    }
  }

  async createStripeCheckoutSession(payload: {
    VenueID: string;
    CustomerID?: string;
    DefaultCurrencyID: string;
    Type: number;
    TableID?: string | null;
    PatronID?: string;
    Description?: string;
    OrderItems: any[];
    TipAmount: number;
    DeliveryFee: number;
    CustomerEmail?: string;
    SuccessURL: string;
    CancelURL: string;
  }): Promise<{ checkout_url: string; session_id: string; online_order_id: string }> {
    try {
      return await this.pb.send<{ checkout_url: string; session_id: string; online_order_id: string }>(
        '/v1/stripe/qr-menu-checkout',
        {
          method: 'POST',
          body: payload,
        }
      );
    } catch (err: any) {
      let errDetail = 'Failed to initiate Stripe checkout.';
      const res = err?.response || err?.data;
      if (res) {
        errDetail = res.message || res.error || (res.data && res.data.message) || errDetail;
      } else if (err?.message) {
        errDetail = err.message;
      }
      throw new Error(errDetail);
    }
  }

  async verifyStripeOrder(sessionId: string, customerId: string): Promise<{
    success: boolean;
    order?: OnlineOrder;
    status?: string;
    message?: string;
  }> {
    try {
      return await this.pb.send<{
        success: boolean;
        order?: OnlineOrder;
        status?: string;
        message?: string;
      }>('/v1/stripe/qr-menu-verify', {
        method: 'POST',
        body: {
          session_id: sessionId,
          venue_id: customerId,
        },
      });
    } catch (err: any) {
      let errDetail = 'Failed to verify Stripe order.';
      const res = err?.response || err?.data;
      if (res) {
        errDetail = res.message || res.error || (res.data && res.data.message) || errDetail;
      } else if (err?.message) {
        errDetail = err.message;
      }
      throw new Error(errDetail);
    }
  }

  async sendNotification(payload: NotificationRequest): Promise<NotificationResponse> {
    try {
      return await this.pb.send<NotificationResponse>('/v1/Notification', {
        method: 'POST',
        body: payload,
      });
    } catch (err: any) {
      const status =
        err?.status ||
        err?.statusCode ||
        err?.response?.code ||
        err?.response?.status;
      const responseData = err?.response || err?.data;
      const remainingSeconds =
        responseData?.data?.remainingSeconds ??
        responseData?.remainingSeconds;
      const message =
        responseData?.message ||
        err?.message ||
        'Failed to send notification.';

      const customError: any = new Error(message);
      customError.status = status;
      customError.remainingSeconds = remainingSeconds;
      customError.responseData = responseData;
      throw customError;
    }
  }

  async subscribeOnlineOrders(
    topic: string,
    callback: (data: RecordSubscription<OnlineOrder>) => void,
    options?: RecordSubscribeOptions
  ): Promise<UnsubscribeFunc> {
    const defaultHeaders: Record<string, string> = {
      api_key: environment.API_KEY,
    };
    if (typeof window !== 'undefined' && window.location) {
      defaultHeaders['referer'] = window.location.href;
    }

    const mergedOptions: RecordSubscribeOptions = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...(options?.headers || {}),
      },
    };

    return await this.pb.collection('OnlineOrder').subscribe(topic, callback as any, mergedOptions);
  }

  async unsubscribeOnlineOrders(topic?: string): Promise<void> {
    return await this.pb.collection('OnlineOrder').unsubscribe(topic);
  }

  get client() {
    return this.pb;
  }
}

export interface PaginatedResult<T> {
  items: T[];
  page: number;
  perPage: number;
  totalItems: number;
  totalPages: number;
}

export interface GetOnlineOrdersOptions {
  page?: number;
  perPage?: number;
  filter?: string;
  sort?: string;
  expand?: string;
}

interface GetMenusOptions {
  customerID: string;
  venueID?: string;
  page?: number;
  perPage?: number;
  filter?: string;
}

interface GetCategoriesOptions {
  customerID: string;
  venueID?: string;
  menuID?: string;
  parentCategoryID?: string | null;
  page?: number;
  perPage?: number;
  filter?: string;
}

interface GetProductsOptions {
  customerID: string;
  venueID?: string;
  menuID?: string;
  categoryID?: string;
  page?: number;
  perPage?: number;
  filter?: string;
}

interface GetOptionGroupsOptions {
  customerID: string;
  venueID?: string;
  menuID?: string;
  page?: number;
  perPage?: number;
  filter?: string;
}

interface GetOptionsOptions {
  customerID: string;
  venueID?: string;
  optionGroupID?: string;
  page?: number;
  perPage?: number;
}
